// src/redux/dynamicField/dynamicFieldSlice.js
import { createSlice } from "@reduxjs/toolkit";
import {
  fetchAllDynamicFields,
  fetchDynamicFieldById,
  createDynamicFieldById,
  updateDynamicFieldById,
  deleteDynamicFieldById,
  toggleDynamicFieldStatus,
  deleteDynamicFieldAlls,
} from "./dynamicFieldThunks";

const dynamicFieldSlice = createSlice({
  name: "dynamicField",
  initialState: {
    list: [],
    totalPages: 0,
    totalFields: 0,
    current: null, // for view/edit
    loading: false,
    error: null,
    pagination: { page: 1, limit: 10, total: 0 },

    // 👇 add filters state
    filters: {
      currentPage: 1,
      itemsPerPage: 10,
      searchQuery: "",
      sortColumn: "createdAt",
      sortOrder: "desc",
      statusFilter: "",
      startDate: null,
      endDate: null,
    },
  },
  reducers: {
    clearDynamicField: (state) => {
      state.current = null;
      state.error = null;
    },

    // 👇 reducer to update filters
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      // ✅ Fetch all
      .addCase(fetchAllDynamicFields.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllDynamicFields.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload.data || [];
        state.pagination.total = action.payload.metadata.total || 0;
        state.totalFields = action.payload.metadata.total || 0; // <- fix here
        state.totalPages = action.payload.metadata.totalPages || 1;
      })
      .addCase(fetchAllDynamicFields.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ✅ Fetch by id
      .addCase(fetchDynamicFieldById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchDynamicFieldById.fulfilled, (state, action) => {
        state.loading = false;
        state.current = action.payload;
      })
      .addCase(fetchDynamicFieldById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // ✅ Create
      .addCase(createDynamicFieldById.pending, (state) => {
        state.loading = true;
      })
      .addCase(createDynamicFieldById.fulfilled, (state, action) => {
        state.loading = false;
        state.list.unshift(action.payload);
      })
      .addCase(createDynamicFieldById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // update
      .addCase(updateDynamicFieldById.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateDynamicFieldById.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload;
        const index = state.list.findIndex((f) => f._id === updated._id);
        if (index !== -1) {
          state.list[index] = updated;
        }
      })

      .addCase(updateDynamicFieldById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ✅ Delete single
      .addCase(deleteDynamicFieldById.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteDynamicFieldById.fulfilled, (state, action) => {
        state.loading = false;
        state.list = state.list.filter((f) => f._id !== action.payload);
        if (state.current && state.current._id === action.payload) {
          state.current = null;
        }
      })
      .addCase(deleteDynamicFieldById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ✅ Change status
      .addCase(toggleDynamicFieldStatus.pending, (state) => {
        state.loading = true;
      })
      .addCase(toggleDynamicFieldStatus.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.list.findIndex((f) => f._id === action.payload.id);
        if (idx > -1) {
          state.list[idx].isActive = action.payload.isActive; // 👈 directly assign
        }
      })
      .addCase(toggleDynamicFieldStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ✅ Bulk delete
      .addCase(deleteDynamicFieldAlls.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteDynamicFieldAlls.fulfilled, (state, action) => {
        state.loading = false;
        state.list = state.list.filter(
          (f) => !action.payload.ids.includes(f._id)
        );
      })
      .addCase(deleteDynamicFieldAlls.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

// 👇 export both actions
export const { clearDynamicField, setFilters } = dynamicFieldSlice.actions;

export default dynamicFieldSlice.reducer;
